'use client';

import { useEffect, useState } from 'react';

interface PageLoaderProps {
  isLoading: boolean;
}

export default function PageLoader({ isLoading }: PageLoaderProps) {
  const [show, setShow] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for prefers-reduced-motion
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      setFadeOut(true);
      const timer = setTimeout(() => {
        setShow(false);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  if (!show) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-primary via-primary-dark to-secondary transition-opacity duration-800 overflow-hidden ${
        fadeOut ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
    >
      {/* Radial gradient overlay for depth */}
      <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black opacity-40 pointer-events-none" />
      
      {/* Main loader content */}
      <div className="relative z-10 flex flex-col items-center gap-8">
        {/* Spinning logo/circle with enhanced glow */}
        <div className="relative w-24 h-24">
          {/* Outer rotating ring with glow - static if reduced motion */}
          <div className={`absolute inset-0 rounded-full border-4 border-transparent border-t-accent border-r-accent shadow-glow-cyan ${
            prefersReducedMotion ? '' : 'animate-spin'
          }`} />
          
          {/* Middle rotating ring - static if reduced motion */}
          <div className={`absolute inset-2 rounded-full border-4 border-transparent border-b-secondary border-l-secondary shadow-glow-blue ${
            prefersReducedMotion ? '' : 'animate-spin-slow'
          }`} />
          
          {/* Inner pulsing circle - static if reduced motion */}
          <div className={`absolute inset-4 rounded-full bg-gradient-to-br from-accent to-secondary ${
            prefersReducedMotion ? '' : 'animate-pulse'
          }`} />
          
          {/* Center dot - static if reduced motion */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className={`w-3 h-3 bg-white rounded-full ${
              prefersReducedMotion ? '' : 'animate-ping'
            }`} />
          </div>
        </div>

        {/* Loading text */}
        <div className="flex flex-col items-center gap-2">
          <h2 className={`text-2xl font-bold text-white drop-shadow-glow ${
            prefersReducedMotion ? '' : 'animate-pulse'
          }`}>
            Loading
          </h2>
          
          {/* Animated dots - static if reduced motion */}
          <div className="flex gap-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`w-2 h-2 bg-accent rounded-full shadow-glow-cyan ${
                  prefersReducedMotion ? '' : 'animate-bounce'
                }`}
                style={{
                  animationDelay: prefersReducedMotion ? undefined : `${i * 0.15}s`,
                }}
              />
            ))}
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-64 h-1 bg-white/20 rounded-full overflow-hidden shadow-lg">
          <div className={`h-full bg-gradient-to-r from-accent via-white to-accent ${
            prefersReducedMotion ? 'w-full' : 'animate-progress'
          }`} />
        </div>
      </div>
    </div>
  );
}